import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

// Email via send.php

const leistungOptioneDE = [
  { id: "aufstockung", label: "Aufstockung / Anbau", icon: "↑", hint: "Mehr Raum ohne Neubau" },
  { id: "dachstuhl", label: "Dachstuhl / Holztragwerk", icon: "△", hint: "Neubau oder Sanierung" },
  { id: "holzhaus", label: "Holzhaus", icon: "⌂", hint: "Individuell geplant" },
  { id: "hotelbau", label: "Hotel- / Tourismusbau", icon: "★", hint: "Im laufenden Betrieb" },
  { id: "holzfassade", label: "Holzfassade", icon: "▣", hint: "Neubau oder Sanierung" },
  { id: "sanierung", label: "Sanierung / Restaurierung", icon: "↻", hint: "Bestand erhalten" },
  { id: "gewerbehalle", label: "Halle / Gewerbe", icon: "▬", hint: "Wirtschaftlich & schnell" },
  { id: "terrasse", label: "Terrasse / Balkon", icon: "⬚", hint: "Draußen, aber zuhause" },
  { id: "unsicher", label: "Noch nicht sicher", icon: "?", hint: "Wir beraten Sie gerne" },
];

const leistungOptioneIT = [
  { id: "aufstockung", label: "Sopraelevazione / Ampliamento", icon: "↑", hint: "Più spazio senza nuova costruzione" },
  { id: "dachstuhl", label: "Capriata / Struttura in legno", icon: "△", hint: "Nuova costruzione o risanamento" },
  { id: "holzhaus", label: "Casa in legno", icon: "⌂", hint: "Pianificata individualmente" },
  { id: "hotelbau", label: "Edilizia alberghiera / turistica", icon: "★", hint: "Durante l'attività" },
  { id: "holzfassade", label: "Facciata in legno", icon: "▣", hint: "Nuova costruzione o risanamento" },
  { id: "sanierung", label: "Risanamento / Restauro", icon: "↻", hint: "Preservare il patrimonio" },
  { id: "gewerbehalle", label: "Capannone / Edificio industriale", icon: "▬", hint: "Economico & veloce" },
  { id: "terrasse", label: "Terrazza / Balcone", icon: "⬚", hint: "Fuori, ma come a casa" },
  { id: "unsicher", label: "Non ancora sicuri", icon: "?", hint: "Siamo lieti di consigliarvi" },
];

const startZeitDE = ["So bald wie möglich", "In 3–6 Monaten", "In 6–12 Monaten", "Noch offen / In Planung"];
const startZeitIT = ["Il prima possibile", "In 3–6 mesi", "In 6–12 mesi", "Ancora aperto / In pianificazione"];

const initialForm = {
  leistung: "",
  startZeit: "",
  flaeche: "",
  besonderheiten: "",
  name: "",
  email: "",
  telefon: "",
  ort: "",
};

export default function Anfrage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { lang } = useLanguage();
  const navigate = useNavigate();

  const leistungOptionen = lang === 'it' ? leistungOptioneIT : leistungOptioneDE;
  const startZeitOptionen = lang === 'it' ? startZeitIT : startZeitDE;
  const steps = lang === 'it'
    ? ["Servizio", "Dettagli", "Contatto"]
    : ["Leistung", "Details", "Kontakt"];

  const canNext = () => {
    if (step === 0) return !!form.leistung;
    if (step === 1) return true;
    if (step === 2) return !!(form.name && form.email);
    return false;
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      setError(lang === 'it' ? "Compilate nome ed e-mail." : "Bitte Name und E-Mail angeben.");
      return;
    }
    setLoading(true);
    setError('');
    const projektart = leistungOptionen.find(l => l.id === form.leistung)?.label || form.leistung;
    try {
      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: 'anfrage', leistung: projektart }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || (lang === 'it' ? 'Errore durante l\'invio.' : 'Fehler beim Senden.'));
      }
    } catch (_) {
      setError(lang === 'it'
        ? 'Errore di connessione. Scrivete direttamente a info@holzbuddy.team'
        : 'Verbindungsfehler. Bitte schreiben Sie direkt an info@holzbuddy.team'
      );
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hb-off-white pt-20">
        <div className="max-w-md w-full mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-hb-wood/15 flex items-center justify-center mx-auto mb-8">
            <Check size={28} className="text-hb-wood" />
          </div>
          <h1 className="text-3xl font-manrope font-extrabold text-hb-dark mb-4">
            <T de="Anfrage erhalten." it="Richiesta ricevuta." />
          </h1>
          <p className="text-hb-text font-inter mb-2">
            <T de="Vielen Dank," it="Grazie mille," /> <strong>{form.name}</strong>.
          </p>
          <p className="text-hb-text font-inter mb-8">
            <T de="Das Holzbuddy Team meldet sich in der Regel innerhalb von 24 Stunden persönlich bei Ihnen."
               it="Il team Holzbuddy vi contatterà di norma entro 24 ore." />
          </p>
          <div className="flex flex-col gap-3">
            <a href="tel:+390473238030" className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={13} /> +39 0473 238 030
            </a>
            <Link to="/" className="btn-link text-center">
              <T de="Zur Startseite" it="Alla pagina iniziale" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hb-off-white pt-20 md:pt-24">
      {/* Header */}
      <div className="bg-white border-b border-hb-border">
        <div className="container-hb py-8">
          <span className="label-text text-hb-wood block mb-2">
            <T de="Projektanfrage" it="Richiesta di progetto" />
          </span>
          <h1 className="text-2xl sm:text-3xl font-manrope font-extrabold text-hb-dark" data-testid="anfrage-heading">
            <T de="Was möchten Sie bauen?" it="Cosa volete costruire?" />
          </h1>
        </div>
      </div>

      {/* Step indicator */}
      <div className="bg-white border-b border-hb-border sticky top-16 md:top-20 z-30">
        <div className="container-hb py-4">
          <div className="flex items-center gap-3">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`flex items-center gap-2 ${i <= step ? 'text-hb-dark' : 'text-hb-muted'}`}>
                  <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold font-mono transition-colors ${
                    i < step ? 'bg-hb-wood text-white' :
                    i === step ? 'bg-hb-dark text-white' :
                    'bg-hb-border text-hb-muted'
                  }`}>
                    {i < step ? <Check size={12} /> : i + 1}
                  </div>
                  <span className="text-xs font-semibold tracking-widest uppercase font-inter hidden sm:block">{s}</span>
                </div>
                {i < steps.length - 1 && <div className={`h-px w-8 ${i < step ? 'bg-hb-wood' : 'bg-hb-border'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="container-hb py-12">
        <div className="max-w-2xl mx-auto">

          {/* Step 0 */}
          {step === 0 && (
            <div data-testid="step-leistung">
              <h2 className="text-xl font-manrope font-bold text-hb-dark mb-8">
                <T de="Welche Leistung interessiert Sie?" it="Quale servizio vi interessa?" />
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {leistungOptionen.map(opt => (
                  <button
                    key={opt.id}
                    data-testid={`leistung-opt-${opt.id}`}
                    onClick={() => setForm(f => ({ ...f, leistung: opt.id }))}
                    className={`flex items-start gap-4 p-5 border text-left transition-all duration-150 ${
                      form.leistung === opt.id
                        ? 'border-hb-dark bg-hb-dark text-white'
                        : 'border-hb-border bg-white hover:border-hb-dark'
                    }`}
                  >
                    <span className="text-xl font-manrope mt-0.5 text-hb-wood">{opt.icon}</span>
                    <div>
                      <div className={`font-manrope font-bold text-sm ${form.leistung === opt.id ? 'text-white' : 'text-hb-dark'}`}>
                        {opt.label}
                      </div>
                      <div className={`text-xs font-inter mt-0.5 ${form.leistung === opt.id ? 'text-white/60' : 'text-hb-muted'}`}>
                        {opt.hint}
                      </div>
                    </div>
                    {form.leistung === opt.id && <Check size={14} className="text-hb-wood ml-auto mt-0.5 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 1 */}
          {step === 1 && (
            <div data-testid="step-details">
              <h2 className="text-xl font-manrope font-bold text-hb-dark mb-8">
                <T de="Ein paar Details zu Ihrem Vorhaben" it="Alcuni dettagli sul vostro progetto" />
              </h2>
              <div className="space-y-8">
                <div>
                  <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-3">
                    <T de="Wann möchten Sie starten?" it="Quando volete iniziare?" />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {startZeitOptionen.map(opt => (
                      <button
                        key={opt}
                        data-testid={`startzeit-${opt}`}
                        onClick={() => setForm(f => ({ ...f, startZeit: opt }))}
                        className={`p-4 border text-left text-sm font-inter transition-all ${
                          form.startZeit === opt
                            ? 'border-hb-dark bg-hb-dark text-white'
                            : 'border-hb-border bg-white hover:border-hb-dark text-hb-text'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                    <T de="Ungefähre Fläche / Größe" it="Superficie / Dimensione approssimativa" />{' '}
                    <span className="normal-case text-hb-muted/70 tracking-normal">
                      (<T de="optional" it="opzionale" />)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form.flaeche}
                    onChange={e => setForm(f => ({ ...f, flaeche: e.target.value }))}
                    placeholder={lang === 'it' ? 'Es. 80 m², 3 locali...' : 'z.B. 80 m², 3 Zimmer...'}
                    data-testid="input-flaeche"
                    className="input-hb"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                    <T de="Besonderheiten oder Wünsche" it="Particolarità o desideri" />{' '}
                    <span className="normal-case text-hb-muted/70 tracking-normal">
                      (<T de="optional" it="opzionale" />)
                    </span>
                  </label>
                  <textarea
                    value={form.besonderheiten}
                    onChange={e => setForm(f => ({ ...f, besonderheiten: e.target.value }))}
                    placeholder={lang === 'it'
                      ? 'Descrivete brevemente il vostro progetto, requisiti particolari o domande...'
                      : 'Beschreiben Sie kurz Ihr Vorhaben, besondere Anforderungen oder Fragen...'
                    }
                    rows={4}
                    data-testid="input-besonderheiten"
                    className="input-hb resize-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div data-testid="step-kontakt">
              <h2 className="text-xl font-manrope font-bold text-hb-dark mb-8">
                <T de="Wie erreichen wir Sie?" it="Come possiamo contattarvi?" />
              </h2>
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      <T de="Name" it="Nome" /> <span className="text-hb-wood">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={lang === 'it' ? 'Il vostro nome' : 'Ihr Name'}
                      data-testid="input-name"
                      className="input-hb"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      E-Mail <span className="text-hb-wood">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder={lang === 'it' ? 'vostro@email.com' : 'ihre@email.com'}
                      data-testid="input-email"
                      className="input-hb"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      <T de="Telefon" it="Telefono" />
                    </label>
                    <input
                      type="tel"
                      value={form.telefon}
                      onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))}
                      placeholder="+39 ..."
                      data-testid="input-telefon"
                      className="input-hb"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      <T de="Ort / Gemeinde" it="Località / Comune" />
                    </label>
                    <input
                      type="text"
                      value={form.ort}
                      onChange={e => setForm(f => ({ ...f, ort: e.target.value }))}
                      placeholder={lang === 'it' ? 'Es. Merano, Bolzano...' : 'z.B. Meran, Bozen...'}
                      data-testid="input-ort"
                      className="input-hb"
                    />
                  </div>
                </div>

                <div className="bg-hb-off-white p-5">
                  <p className="text-xs text-hb-text font-inter leading-relaxed">
                    <T de="Mit dem Absenden stimmen Sie zu, dass Holzbuddy Ihre Daten zur Bearbeitung Ihrer Anfrage verwendet. Wir geben Ihre Daten nicht weiter."
                       it="Inviando il modulo, accettate che Holzbuddy utilizzi i vostri dati per elaborare la vostra richiesta. Non trasmettiamo i vostri dati a terzi." />
                  </p>
                </div>

                {error && <p data-testid="form-error" className="text-sm text-red-500 font-inter">{error}</p>}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-8 border-t border-hb-border">
            <button
              onClick={() => step > 0 && setStep(s => s - 1)}
              disabled={step === 0}
              data-testid="btn-back"
              className={`flex items-center gap-2 text-xs tracking-widest uppercase font-semibold transition-colors ${
                step === 0 ? 'text-hb-muted cursor-not-allowed' : 'text-hb-dark hover:text-hb-wood'
              }`}
            >
              <ArrowLeft size={13} /> <T de="Zurück" it="Indietro" />
            </button>

            {step < 2 ? (
              <button
                onClick={() => step < 2 && setStep(s => s + 1)}
                disabled={!canNext()}
                data-testid="btn-next"
                className={`btn-primary flex items-center gap-2 ${!canNext() ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <T de="Weiter" it="Avanti" /> <ArrowRight size={13} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !form.name || !form.email}
                data-testid="btn-submit"
                className={`btn-primary ${(loading || !form.name || !form.email) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading
                  ? <T de="Wird gesendet..." it="Invio in corso..." />
                  : <T de="Anfrage senden" it="Invia richiesta" />
                }
              </button>
            )}
          </div>

          <p className="text-center mt-6 text-xs text-hb-muted font-inter">
            <T de="Lieber direkt?" it="Preferite contattarci direttamente?" />{" "}
            <Link to="/kontakt" className="underline hover:text-hb-dark transition-colors">
              <T de="Kontaktformular öffnen" it="Apri il modulo di contatto" />
            </Link>{" "}
            <T de="oder" it="oppure" />{" "}
            <a href="tel:+390473238030" className="underline hover:text-hb-dark transition-colors">
              <T de="anrufen" it="chiamare" />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
